import { useEffect, useMemo } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/ui/button'
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/ui/dialog'
import { Input } from '@/ui/input'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/ui/form'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/ui/select'
import { RadioGroup, RadioGroupItem } from '@/ui/radio-group'
import { Label } from '@/ui/label'
import type { CreateDataFlowScheduleRequest } from '@/features/adp/services/api-service.types'
import { useAppSelector } from '@/app/reduxHooks'
import { useCreateDataFlowScheduleMutation } from '@/features/adp/hooks/useCreateDataFlowScheduleMutation'
import { useRunJobMutation } from '@/features/adp/hooks/useRunJobMutation'
import { toast } from 'sonner'
import type { PipelineItem } from '@/features/adp/api/pipelineLists.api'
import { useDispatch } from 'react-redux'
import { closeOverlay, type SchedulePipelineOverlayData } from '@/features/adp/state/overlaySlice'

const schedulePipelineSchema = z
    .object({
        mode: z.enum(['onetime', 'repeated']),
        startDate: z.string().optional(),
        startTime: z.string().optional(),
        frequency: z.enum(['daily', 'hourly', 'weekly', 'minute-wise']).optional(),
        frequencyhrs: z.number().optional(),
    })
    .superRefine((values, ctx) => {
        if (!values.startDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['startDate'],
                message: 'Start date is required for scheduled runs.',
            })
        }

        if (!values.startTime) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['startTime'],
                message: 'Start time is required for scheduled runs.',
            })
        }

        if (values.startDate) {
            const todayDate = getTodayDateString()
            if (values.startDate < todayDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['startDate'],
                    message: 'Selected date cannot be in the past.',
                })
            }
        }

        if (values.mode === 'repeated' && !values.frequency) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['frequency'],
                message: 'Frequency is required for repeated schedule.',
            })
        }

        if (
            values.mode === 'repeated' &&
            (values.frequency === 'hourly' || values.frequency === 'minute-wise') &&
            (!values.frequencyhrs || values.frequencyhrs <= 0)
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['frequencyhrs'],
                message: values.frequency === 'hourly'
                    ? 'Frequency hours is required.'
                    : 'Frequency minutes is required.',
            })
        }

        if (values.startDate && values.startTime) {
            const selectedDateTime = new Date(`${values.startDate}T${values.startTime}`)
            const currentDateTime = new Date()
            if (
                !Number.isNaN(selectedDateTime.getTime()) &&
                values.startDate === getTodayDateString() &&
                selectedDateTime < currentDateTime
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['startTime'],
                    message: 'Selected time cannot be in the past for current date.',
                })
            }
        }
    })

type SchedulePipelineFormValues = z.infer<typeof schedulePipelineSchema>

function padNumber(value: number) {
    return String(value).padStart(2, '0')
}

function getTodayDateString() {
    const now = new Date()
    return `${now.getFullYear()}-${padNumber(now.getMonth() + 1)}-${padNumber(now.getDate())}`
}

function buildFormValuesFromSchedule(
    isSchedule?: boolean,
    scheduleDetails?: PipelineItem['schedule_details'],
    scheduleTypeName?: string | null
): SchedulePipelineFormValues {
    if (!scheduleDetails) {
        return {
            mode: isSchedule ? 'repeated' : 'onetime',
            frequency: 'daily',
            frequencyhrs: 5,
            startDate: '',
            startTime: '',
        }
    }

    const normalizedType = scheduleTypeName?.toLowerCase() ?? ''
    const frequencyHours = Number(scheduleDetails.frequency_hours ?? 0)
    const frequencyMinutes = Number(scheduleDetails.frequency_minutes ?? 0)
    const isRepeated =
        Boolean(isSchedule) || normalizedType.includes('repeated') || frequencyHours > 0 || frequencyMinutes > 0

    let frequency: SchedulePipelineFormValues['frequency'] = 'daily'
    let frequencyhrs = 5

    if (frequencyMinutes > 0) {
        frequency = 'minute-wise'
        frequencyhrs = frequencyMinutes
    } else if (frequencyHours > 0) {
        frequency = 'hourly'
        frequencyhrs = frequencyHours
    } else if (normalizedType.includes('weekly')) {
        frequency = 'weekly'
    } else if (normalizedType.includes('daily')) {
        frequency = 'daily'
    }

    return {
        mode: isRepeated ? 'repeated' : 'onetime',
        frequency,
        frequencyhrs,
        startDate: `${scheduleDetails.start_year}-${padNumber(scheduleDetails.start_month)}-${padNumber(scheduleDetails.start_day)}`,
        startTime: `${padNumber(scheduleDetails.start_time_hour)}:${padNumber(scheduleDetails.start_time_minutes)}`,
    }
}

const SchedulePipeline = () => {
    const dispatch = useDispatch()
    const overlayState = useAppSelector(state => state.overlay)
    const overlayData = (overlayState.type === 'schedule-pipeline-modal' ? overlayState.data : null) as SchedulePipelineOverlayData | null
    const isOpen = overlayState.isOpen && overlayState.type === 'schedule-pipeline-modal'
    const defaultFormValues = useMemo(
        () =>
            buildFormValuesFromSchedule(
                overlayData?.isSchedule,
                overlayData?.scheduleDetails,
                overlayData?.scheduleTypeName
            ),
        [overlayData?.isSchedule, overlayData?.scheduleDetails, overlayData?.scheduleTypeName]
    )
    const { mutateAsync: createDataFlowSchedule, isPending: isCreatingSchedule } =
        useCreateDataFlowScheduleMutation()
    const { mutateAsync: runJob, isPending: isRunningJob } = useRunJobMutation()
    const form = useForm<SchedulePipelineFormValues>({
        resolver: zodResolver(schedulePipelineSchema),
        mode: 'onChange',
        defaultValues: defaultFormValues,
    })

    const mode = form.watch('mode');
    const frequency = form.watch('frequency');
    const isEditMode = Boolean(overlayData?.scheduleDetails)
    const isSaveDisabled =
        !form.formState.isValid ||
        form.formState.isSubmitting ||
        isCreatingSchedule ||
        isRunningJob ||
        (isEditMode && !form.formState.isDirty);

    const hourlyValues = Array.from({ length: 24 }, (_, idx) => idx + 1);
    const minuteValues = Array.from({ length: 59 }, (_, idx) => idx + 1);

    const { pipelineName, sessionId } = useAppSelector(state => state.pipeline);
    const effectivePipelineName = overlayData?.pipelineName ?? pipelineName
    const effectiveSessionId = overlayData?.sessionId ?? sessionId

    useEffect(() => {
        if (isOpen) {
            form.reset(defaultFormValues)
        }
    }, [defaultFormValues, form, isOpen])

    const handleDialogOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            form.reset(defaultFormValues)
            dispatch(closeOverlay())
        }
    }

    const getScheduleTypeId = (values: SchedulePipelineFormValues): number => {
        if (values.mode === 'onetime') return 1
        if (values.frequency === 'hourly') return 3
        if (values.frequency === 'daily') return 4
        if (values.frequency === 'weekly') return 5
        return 2
    };

    const handleSubmit = async (values: SchedulePipelineFormValues) => {
        if (!values.startDate || !values.startTime) return

        const startDate = new Date(values.startDate);
        const [hourStr, minuteStr] = values.startTime.split(':');
        const startHour = Number(hourStr);
        const startMin = Number(minuteStr);
        const scheduleTypeId = getScheduleTypeId(values);
        const frequencyValue = values.frequencyhrs ?? 0;

        if (!effectivePipelineName?.trim()) {
            toast.error('Pipeline name is missing. Please try again.')
            return
        }

        const payload: CreateDataFlowScheduleRequest = {
            job_schedular: [
                {
                    data_flow_name: effectivePipelineName,
                    start_day: startDate.getDate(),
                    start_month: startDate.getMonth() + 1,
                    start_year: startDate.getFullYear(),
                    start_time_hour: startHour,
                    start_time_minutes: startMin,
                    frequency_hours: values.frequency === 'hourly' ? frequencyValue : 0,
                    frequency_minutes: values.frequency === 'minute-wise' ? frequencyValue : 0,
                    frequency_day_of_week: startDate.getDay(),
                    schedule_type_id: scheduleTypeId,
                },
            ],
        }

        try {
            await createDataFlowSchedule(payload)
        } catch {
            toast.error('Unable to schedule the pipeline right now. Please try again.')
            return
        }

        try {
            await runJob(effectiveSessionId ?? '')
        } catch {
            toast.error('Pipeline is scheduled, but we could not trigger it right now. Please try again.')
            return
        }

        toast.success('Pipeline scheduled and triggered successfully.')
        handleDialogOpenChange(false)
    }

    return (
        <Dialog
            open={isOpen}
            onOpenChange={handleDialogOpenChange}
        >
            <DialogContent
                className="max-w-2xl overflow-hidden p-0"
                onOpenAutoFocus={(event) => {
                    event.preventDefault()
                }}
            >
                <DialogHeader className="border-b px-6 py-4">
                    <DialogTitle>Schedule Pipeline</DialogTitle>
                    <DialogDescription>
                        Configure when and how this pipeline should run.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 px-6 py-5">
                        <FormField
                            control={form.control}
                            name="mode"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Mode</FormLabel>
                                    <FormControl>
                                        <RadioGroup
                                            value={field.value}
                                            onValueChange={(value) => {
                                                field.onChange(value)
                                                if (value === 'onetime') {
                                                    form.setValue('frequency', 'daily')
                                                    form.setValue('frequencyhrs', 5)
                                                    form.clearErrors(['frequency', 'frequencyhrs'])
                                                }
                                            }}
                                            className="flex items-center gap-6 pt-1"
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="onetime" id="schedule-mode-onetime" />
                                                <Label htmlFor="schedule-mode-onetime" className="text-base font-normal">
                                                    One Time
                                                </Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="repeated" id="schedule-mode-repeated" />
                                                <Label htmlFor="schedule-mode-repeated" className="text-base font-normal">
                                                    Repeated
                                                </Label>
                                            </div>
                                        </RadioGroup>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <FormField
                                control={form.control}
                                name="startDate"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Start Date</FormLabel>
                                        <FormControl>
                                            <Input
                                                type="date"
                                                {...field}
                                                value={field.value ?? ''}
                                                min={getTodayDateString()}
                                                className="focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#3B82F6]"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="startTime"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Start Time</FormLabel>
                                        <FormControl>
                                            <Input
                                                type="time"
                                                {...field}
                                                value={field.value ?? ''}
                                                className="focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#3B82F6]"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {mode === 'repeated' && (
                            <FormField
                                control={form.control}
                                name="frequency"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Frequency</FormLabel>
                                        <Select
                                            value={field.value}
                                            onValueChange={(value: 'daily' | 'hourly' | 'weekly' | 'minute-wise') =>
                                                field.onChange(value)
                                            }
                                        >
                                            <FormControl>
                                                <SelectTrigger className="bg-[#F1F5F9]">
                                                    <SelectValue placeholder="Select frequency" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="daily">Daily</SelectItem>
                                                <SelectItem value="hourly">Hourly</SelectItem>
                                                <SelectItem value="weekly">Weekly</SelectItem>
                                                <SelectItem value="minute-wise">Minute-Wise</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}

                        {mode === 'repeated' && (frequency === 'hourly' || frequency === 'minute-wise') && (
                            <FormField
                                control={form.control}
                                name="frequencyhrs"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Frequency {frequency === 'hourly' ? 'Hours' : 'Minutes'}
                                        </FormLabel>
                                        <Select
                                            value={field.value ? String(field.value) : ''}
                                            onValueChange={(value) => field.onChange(Number(value))}
                                        >
                                            <FormControl>
                                                <SelectTrigger className="bg-[#F1F5F9]">
                                                    <SelectValue
                                                        placeholder={
                                                            frequency === 'hourly'
                                                                ? 'Select frequency hours'
                                                                : 'Select frequency minutes'
                                                        }
                                                    />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {(frequency === 'hourly' ? hourlyValues : minuteValues).map((value) => (
                                                    <SelectItem key={value} value={String(value)}>
                                                        {value}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}

                        <div className="flex justify-end gap-3 border-t pt-5">
                            <DialogClose asChild>
                                <Button type="button" variant="outline">
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button type="submit" disabled={isSaveDisabled}>Save</Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default SchedulePipeline
